// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { interviewAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Loader2, Clock, Users, Copy, Trash2, Edit, FileText, Layers, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const LEVEL_LABELS = { junior: "Junior", confirmed: "Confirmé", senior: "Senior", expert: "Expert" };
const STATUS_LABELS = { draft: "Brouillon", published: "Publié", archived: "Archivé" };
const STATUS_COLORS = { draft: "bg-yellow-100 text-yellow-700", published: "bg-green-100 text-green-700", archived: "bg-gray-100 text-gray-700" };

export default function InterviewTemplatesPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", target_role: "", target_level: "confirmed", duration_minutes: 60 });

  useEffect(() => { loadData(); }, [workspaceId]);

  const loadData = async () => {
    try {
      const res = await interviewAPI.listTemplates(workspaceId);
      setTemplates(res.data);
    } catch (e) { toast.error("Erreur"); } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Le titre est requis"); return; }
    setSaving(true);
    try {
      const res = await interviewAPI.createTemplate(workspaceId, user?.id, form);
      toast.success("Template créé");
      setDialogOpen(false);
      navigate(`/workspace/${workspaceId}/interviews/templates/${res.data.id}`);
    } catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const handleDuplicate = async (id) => {
    try {
      await interviewAPI.duplicateTemplate(id, workspaceId, user?.id);
      toast.success("Template dupliqué");
      loadData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDelete = async () => {
    try {
      await interviewAPI.deleteTemplate(deleteId);
      toast.success("Template supprimé");
      setDeleteId(null);
      loadData();
    } catch (e) { toast.error("Erreur"); }
  };

  if (loading) {
    return <WorkspaceLayout><div className="page-container"><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></div></WorkspaceLayout>;
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/interviews`)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Templates d'entretien</h1>
              <p className="mt-1 text-muted-foreground">{templates.length} template(s)</p>
            </div>
          </div>
          <Button className="gap-2" onClick={() => { setForm({ title: "", description: "", target_role: "", target_level: "confirmed", duration_minutes: 60 }); setDialogOpen(true); }}>
            <Plus className="h-4 w-4" /> Nouveau template
          </Button>
        </div>

        {templates.length === 0 ? (
          <Card className="mt-8 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Layers className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 font-semibold">Aucun template</h3>
              <p className="mt-2 text-sm text-muted-foreground">Créez un modèle d'entretien pour standardiser vos évaluations</p>
              <Button className="mt-4" onClick={() => setDialogOpen(true)}>Créer un template</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((t) => (
              <Card key={t.id} className="hover:shadow-lg transition-all duration-200 cursor-pointer group"
                onClick={() => navigate(`/workspace/${workspaceId}/interviews/templates/${t.id}`)}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{t.title}</CardTitle>
                      {t.target_role && <p className="text-sm text-muted-foreground mt-1">{t.target_role}</p>}
                    </div>
                    <Badge className={STATUS_COLORS[t.status] || ""}>{STATUS_LABELS[t.status] || t.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {t.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{t.description}</p>}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{t.duration_minutes} min</span>
                    <span className="flex items-center gap-1"><Layers className="h-3.5 w-3.5" />{(t.sections || []).length} sections</span>
                    <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{t.question_count || 0} questions</span>
                  </div>
                  <Badge variant="outline" className="mt-3">{LEVEL_LABELS[t.target_level] || t.target_level}</Badge>
                  <div className="mt-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/workspace/${workspaceId}/interviews/templates/${t.id}`)}>
                      <Edit className="h-3.5 w-3.5 mr-1" /> Modifier
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDuplicate(t.id)}>
                      <Copy className="h-3.5 w-3.5 mr-1" /> Dupliquer
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteId(t.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nouveau template d'entretien</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Titre *</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Ex: Entretien DevOps Senior" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={2} /></div>
            <div><Label>Poste visé</Label><Input value={form.target_role} onChange={(e) => setForm({...form, target_role: e.target.value})} placeholder="Ex: DevOps Engineer" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Niveau cible</Label>
                <Select value={form.target_level} onValueChange={(v) => setForm({...form, target_level: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(LEVEL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Durée (min)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({...form, duration_minutes: parseInt(e.target.value) || 60})} /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Créer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Supprimer ce template ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WorkspaceLayout>
  );
}
